import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Typography,
  Spin,
  Card,
  Row,
  Col,
  Button,
  Tag,
  Space,
  Popconfirm,
  message,
  Empty,
  Divider,
} from "antd";
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  MailOutlined,
  GlobalOutlined,
} from "@ant-design/icons";

const { Title, Text } = Typography;

const formatDate = (value) => {
  if (!value) return "N/A";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "N/A";
  return d.toLocaleString();
};

const normalizeWebsite = (url) => {
  if (!url) return null;
  return url.startsWith("http://") || url.startsWith("https://")
    ? url
    : `http://${url}`;
};

function Field({ label, value, fullWidth, preserveWhitespace }) {
  const colProps = fullWidth
    ? { xs: 24 }
    : { xs: 24, sm: 12, md: 8, lg: 6 };

  const hasValue = value !== null && value !== undefined && value !== "";

  return (
    <Col {...colProps}>
      <div
        style={{
          fontSize: 12,
          fontWeight: 500,
          color: "#8c8c8c",
          textTransform: "uppercase",
          letterSpacing: 0.4,
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 15,
          color: "#262626",
          wordBreak: "break-word",
          whiteSpace: preserveWhitespace ? "pre-wrap" : "normal",
          lineHeight: 1.5,
        }}
      >
        {hasValue ? value : <span style={{ color: "#bfbfbf" }}>N/A</span>}
      </div>
    </Col>
  );
}

export default function PartnerOrganizationDetails() {
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const location = useLocation();
  const history = useHistory();

  const id = location?.pathname?.split("/")[2];

  const fetchSubmissionById = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_MASTER}/partner/${id}`
      );
      const data = await response.json();

      if (data.success) {
        setSubmission(data.data);
      } else {
        setSubmission(null);
      }
    } catch {
      setSubmission(null);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchSubmissionById();
  }, [id]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_MASTER}/partner/${id}`,
        { method: "DELETE" }
      );
      const data = await response.json();

      if (data.success) {
        message.success("Submission deleted");
        history.push("/partners");
      } else {
        message.error(data.message || "Failed to delete submission");
      }
    } catch {
      message.error("Failed to delete submission");
    }
    setDeleting(false);
  };

  const websiteHref = normalizeWebsite(submission?.website);

  return (
    <div style={{ padding: "20px", width: "100%" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 16,
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <Space size="middle" align="center">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => history.push("/partners")}
          >
            Back
          </Button>
          <Title level={3} style={{ margin: 0 }}>
            Submission Details
          </Title>
        </Space>

        {submission && (
          <Popconfirm
            title="Delete this submission?"
            description="This action cannot be undone."
            okText="Delete"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
            onConfirm={handleDelete}
          >
            <Button danger icon={<DeleteOutlined />} loading={deleting}>
              Delete
            </Button>
          </Popconfirm>
        )}
      </div>

      {loading && (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin size="large" />
        </div>
      )}

      {!loading && !submission && (
        <Card>
          <Empty description="No Partner Organization details found" />
        </Card>
      )}

      {!loading && submission && (
        <Card
          bordered
          style={{
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
            borderRadius: 8,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              marginBottom: 12,
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <div>
              <Title level={4} style={{ margin: 0 }}>
                {submission.organizationName || "Unnamed Organization"}
              </Title>
              <Text type="secondary">
                Submitted
                {" "}
                {formatDate(submission.createdAt)}
              </Text>
            </div>
            {submission.givingLevel && (
              <Tag color="blue" style={{ fontSize: 14, padding: "4px 10px" }}>
                {submission.givingLevel}
              </Tag>
            )}
          </div>

          <Divider style={{ margin: "16px 0 24px" }} />

          <Row gutter={[24, 24]}>
            <Field label="Organization Name" value={submission.organizationName} />
            <Field label="Contact Name" value={submission.contactName} />
            <Field label="Location" value={submission.location} />
            <Field
              label="Email"
              value={
                submission.email ? (
                  <a href={`mailto:${submission.email}`}>
                    <MailOutlined style={{ marginRight: 6 }} />
                    {submission.email}
                  </a>
                ) : null
              }
            />
            <Field
              label="Website"
              value={
                websiteHref ? (
                  <a
                    href={websiteHref}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <GlobalOutlined style={{ marginRight: 6 }} />
                    {submission.website}
                  </a>
                ) : null
              }
            />
            <Field label="Organization Size" value={submission.organizationSize} />
            <Field label="Area of Interest" value={submission.areaOfInterest} />
            <Field label="How Did You Hear About Us" value={submission.hearAboutUs} />
            <Field label="Giving Level" value={submission.givingLevel} />
            <Field label="Created At" value={formatDate(submission.createdAt)} />
            <Field
              label="Interest Details"
              value={submission.interestDetails}
              fullWidth
              preserveWhitespace
            />
            <Field
              label="Additional Details"
              value={submission.additionalDetails}
              fullWidth
              preserveWhitespace
            />
          </Row>
        </Card>
      )}
    </div>
  );
}
